import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useProjects } from '@/hooks/useProjects';
import { useUIStore } from '@/stores/uiStore';
import type { ProjectFormData } from '@/types';

const projectSchema = z.object({
  name: z.string().min(1, '프로젝트 이름은 필수입니다').max(100),
  description: z.string().max(500).optional(),
});

export function CreateProjectModal() {
  const { modals, closeModal } = useUIStore();
  const { createProject, isCreating } = useProjects();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
  });

  const onSubmit = async (data: ProjectFormData) => {
    setError(null);
    try {
      await createProject(data);
      reset();
      closeModal('createProject');
    } catch (err) {
      setError(err instanceof Error ? err.message : '프로젝트 생성에 실패했습니다');
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    closeModal('createProject');
  };

  return (
    <Dialog open={modals.createProject} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>새 프로젝트 만들기</DialogTitle>
          <DialogDescription>
            워크스페이스에 새 프로젝트를 추가합니다. 세부 사항은 나중에 추가할 수 있습니다.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">프로젝트 이름</Label>
            <Input
              id="name"
              placeholder="나의 멋진 프로젝트"
              error={!!errors.name}
              {...register('name')}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">설명 (선택사항)</Label>
            <Textarea
              id="description"
              placeholder="프로젝트에 대한 간단한 설명..."
              rows={3}
              error={!!errors.description}
              {...register('description')}
            />
            {errors.description && (
              <p className="text-xs text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              취소
            </Button>
            <Button type="submit" loading={isCreating}>
              프로젝트 만들기
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
